import React, { useEffect, useRef, useState } from "react";
// COMPONENT IMPORTS
import Button from "components/form/Button";
import SuccessScreen from "components/ui-component/SuccessScreen";

// ICONS IMPORTS
import { CloseIcon } from "icons/payments/CloseIcon";
import { UserAddIcon } from "icons/payments/UserAddIcon";
import { UserTickIcon } from "icons/payments/UserTickIcon";
import { PaypalIcon } from "icons/dashboard/paypal";
import { AppleIcon } from "icons/dashboard/apple";
import { GoogleIcon } from "icons/dashboard/google";
import { CashAppIcon } from "icons/dashboard/cashAppIcon";
import { MobileVector } from "icons/payments/MobileVector";
import { useAppDispatch, useAppSelector } from "store/hooks";
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { loadStripe, SetupIntent } from "@stripe/stripe-js";
import StripeGooglePayButton from "components/payments/GooglePayButton";
import StripeApplePayButton from "components/payments/ApplePayButton";
import { useLeadData } from "store/slices/dashboardSlice";
import { useUserData } from "store/slices/authSlice";
import {
  updateWalletList,
  useOldWalletData,
  walletsProfile,
} from "store/slices/paymentMethodSlice";
import {
  createPaypalToken,
  getIntent,
  storePayPalOrder,
} from "store/slices/paymentsSlice";
import { useParams } from "react-router-dom";
import { decode } from "utils/HelperFunctions";
import AmazonPay from "icons/dashboard/AmazonPay";
import { PaymentMethodList } from "types/payments";
import FailedScreen from "components/ui-component/FailedScreen";
import { RootState } from "store/store";
import { useTid } from "utils/testid";
import { resolveStripePublishableKey } from "utils/stripePublishableKey";

interface AddEditWalletFormProps {
  data?: any;
  setData?: any;
  show: boolean;
  setShow: any;
  onCloseClick: any;
  setIsEdit?: any;
  isEdit?: boolean;
  isSuccess?: boolean;
  isFailed?: boolean;
  isScreenLoading?: boolean;
}

const AddEditWalletForm = (props: AddEditWalletFormProps) => {
  const tid = useTid();
  // Separate Stripe promises for each wallet type
  const { paymentPermission, companyDetails } = useAppSelector(
    (state: RootState) => state.payment
  );

  const [stripePromiseApple, setStripePromiseApple] = useState<any>(null);
  const [stripePromiseAmazon, setStripePromiseAmazon] = useState<any>(null);
  const [stripePromiseCashApp, setStripePromiseCashApp] = useState<any>(null);
  const [stripePromiseGooglePay, setStripePromiseGooglePay] =
    useState<any>(null);
  const appleKey = resolveStripePublishableKey(paymentPermission, "AP");
  const amazonKey = resolveStripePublishableKey(paymentPermission, "AMZP");
  const cashAppKey = resolveStripePublishableKey(paymentPermission, "CAPP");
  const googlePayKey = resolveStripePublishableKey(paymentPermission, "GP");

  useEffect(() => {
    if (appleKey) {
      setStripePromiseApple(loadStripe(appleKey));
    } else {
      setStripePromiseApple(null);
    }
    if (amazonKey) {
      setStripePromiseAmazon(loadStripe(amazonKey));
    } else {
      setStripePromiseAmazon(null);
    }
    if (cashAppKey) {
      setStripePromiseCashApp(loadStripe(cashAppKey));
    } else {
      setStripePromiseCashApp(null);
    }
    setStripePromiseGooglePay(googlePayKey ? loadStripe(googlePayKey) : null);
  }, [appleKey, amazonKey, cashAppKey, googlePayKey]);

  const [paymentMethodList, setPaymentMethodList] = useState<
    PaymentMethodList[]
  >([]);

  const [selectedMethod, setSelectedMethod] = useState("PayPal");
  // Do not assume Apple Pay is available before Stripe checks the browser.
  // Starting true hides Google Pay and can leave it permanently hidden when
  // Apple Pay is not configured, because the Apple Pay detector never mounts.
  const [isApplePay, setIsApplePay] = useState<boolean>(false);
  const [isApplePayCheckComplete, setIsApplePayCheckComplete] =
    useState<boolean>(false);
  const [isPayPalLoding, setIsPayPalLoading] = useState<boolean>(false);
  const dispatch = useAppDispatch();
  const accountLeadData = useLeadData();
  const userData = useUserData();
  const walletList = useOldWalletData();
  const { clusterId: cluster_id } = useParams();
  const clusterId = decode(cluster_id || "", true);
  const {
    setData,
    show,
    setShow,
    onCloseClick,
    isEdit,
    setIsEdit,
    isSuccess = false,
    isFailed = false,
    isScreenLoading = false,
  } = props;
  const [successScreen, setSuccessScreen] = useState<boolean>(isSuccess);
  const [failedScreen, setFailedScreen] = useState<boolean>(isFailed);
  const [isSecretLoading, setIsSecretLoading] = useState<boolean>(true);
  const [setupClientSecret, setSetupClientSecret] = useState(undefined);
  const [setupAmazonSecret, setSetupAmazonSecret] = useState(undefined);
  const [setupGooglePaySecret, setSetupGooglePaySecret] = useState<
    string | undefined
  >(undefined);
  const [isGooglePayAvailable, setIsGooglePayAvailable] =
    useState<boolean>(true);
  const googlePayIntentFingerprintRef = useRef<string>("");
  const [isBTnLoading, setIsBTNLoading] = useState<boolean>(false);
  const onSubmit = async (e: any) => {
    e.preventDefault();
  };

  const getSecret = async (method: string) => {
    let payment_method_types =
      method === "Amazon Pay"
        ? "amazon_pay"
        : method === "Google Pay"
          ? "card"
          : "cashapp";
    const googlePayIntentFingerprint = [
      userData?.data?.accountData?.accountid || "",
      clusterId || userData?.data?.accountData?.clusterid || "",
      isEdit ? props.data?.id || "edit" : "add",
    ].join(":");
    if (
      method === "Google Pay" &&
      googlePayIntentFingerprintRef.current === googlePayIntentFingerprint
    ) {
      return;
    }
    if (method === "Google Pay") {
      googlePayIntentFingerprintRef.current = googlePayIntentFingerprint;
    }
    try {
      setIsSecretLoading(true);
      let payload = {
        name:
          (userData?.data?.accountData?.first_name || "") +
          (userData?.data?.accountData?.last_name || ""),
        email: userData?.data?.accountData?.email,
        phone_number: userData?.data?.accountData.phone,
        description: "Online Payment",
        payment_note: "One-time",
        currency: "usd",
        total_amount: 0,
        payment_method_types,
        isRecurring: true,
        isWalletProfile: true,
        ...(method === "Google Pay"
          ? {
              gateway_method: "GP",
              isWalletProfile: true,
            }
          : {}),
        accountid: userData?.data?.accountData?.accountid,
        cluster_id: clusterId,
        leadid: accountLeadData?.lead_id,
      };
      let res: any = await dispatch(getIntent(payload));
      if (res?.payload?.body?.success) {
        if (method === "Amazon Pay") {
          setSetupAmazonSecret(res?.payload?.body?.data?.client_secret);
        } else if (method === "Google Pay") {
          setSetupGooglePaySecret(res?.payload?.body?.data?.client_secret);
        } else {
          setSetupClientSecret(res?.payload?.body?.data?.client_secret);
        }
      } else if (method === "Google Pay") {
        googlePayIntentFingerprintRef.current = "";
        setFailedScreen(true);
      }
      setIsSecretLoading(false);
    } catch (err) {
      console.log("err =====", err);
      if (method === "Google Pay") {
        googlePayIntentFingerprintRef.current = "";
        setFailedScreen(true);
      }
      setIsSecretLoading(false);
    }
  };

  useEffect(() => {
    setSuccessScreen(isSuccess);
    setFailedScreen(isFailed);
  }, [isSuccess, isFailed]);

  useEffect(() => {
    if (!show) {
      onClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  const onClose = () => {
    setShow(false);
    setIsEdit(false);
    setData(null);
    setSuccessScreen(false);
    setFailedScreen(false);
    setIsApplePay(false);
    setIsApplePayCheckComplete(false);
    setSetupGooglePaySecret(undefined);
    setIsGooglePayAvailable(true);
    setIsSecretLoading(true);
    googlePayIntentFingerprintRef.current = "";
  };

  useEffect(() => {
    if (show && isEdit && ["AP", "GP"].includes(props.data?.payment_method)) {
      const isGooglePayEdit = props.data?.payment_method === "GP";
      setSelectedMethod(isGooglePayEdit ? "Google Pay" : "Apple Pay");
      setIsApplePay(false);
      setSetupGooglePaySecret(undefined);
      if (isGooglePayEdit) {
        void getSecret("Google Pay");
      }
    }
    // The edit target is the trigger. getSecret intentionally stays outside
    // the dependency list so state changes during intent creation do not
    // create another SetupIntent.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, props.data, show]);

  useEffect(() => {
    let methodList: any = [];
    if (paymentPermission?.pp_ext_availability === "Y") {
      methodList.push({
        id: 1,
        name: "PayPal",
        icon: <PaypalIcon width={20} height={20} />,
        activeIcon: (
          <PaypalIcon
            width={20}
            height={20}
            strokeColor={"var(--color-blue)"}
          />
        ),
        display: true,
      });
    }
    if (paymentPermission?.ap_ext_availability === "Y") {
      methodList.push({
        id: 2,
        name: "Apple Pay",
        icon: <AppleIcon width={20} height={20} />,
        activeIcon: (
          <AppleIcon width={20} height={20} strokeColor={"var(--color-blue)"} />
        ),
        display: isApplePay,
      });
    }
    if (paymentPermission?.gp_ext_availability === "Y") {
      methodList.push({
        id: 3,
        name: "Google Pay",
        icon: <GoogleIcon width={20} height={20} />,
        activeIcon: (
          <GoogleIcon
            width={20}
            height={20}
            strokeColor={"var(--color-blue)"}
          />
        ),
        display:
          paymentPermission?.ap_ext_availability === "Y" &&
          paymentPermission?.ap_gateway_name?.key
            ? isApplePayCheckComplete && !isApplePay
            : true,
      });
    }
    if (paymentPermission?.capp_availability === "Y") {
      methodList.push({
        id: 4,
        name: "Cash App Pay",
        icon: <CashAppIcon width={20} height={20} />,
        display: true,
        activeIcon: (
          <CashAppIcon
            width={20}
            height={20}
            strokeColor={"var(--color-blue)"}
          />
        ),
      });
    }
    if (paymentPermission?.amzp_availability === "Y") {
      methodList.push({
        id: 5,
        name: "Amazon Pay",
        icon: <AmazonPay width={20} height={20} />,
        display: true,
        activeIcon: (
          <AmazonPay width={20} height={20} strokeColor={"var(--color-blue)"} />
        ),
      });
    }
    setPaymentMethodList(
      isEdit && ["AP", "GP"].includes(props.data?.payment_method)
        ? methodList.filter(
            (method: PaymentMethodList) =>
              method.name ===
              (props.data?.payment_method === "AP" ? "Apple Pay" : "Google Pay")
          )
        : methodList
    );
  }, [
    isApplePay,
    isApplePayCheckComplete,
    isEdit,
    paymentPermission?.amzp_availability,
    paymentPermission?.ap_ext_availability,
    paymentPermission?.ap_gateway_name?.key,
    paymentPermission?.capp_availability,
    paymentPermission?.gp_ext_availability,
    paymentPermission?.pp_ext_availability,
    props.data,
  ]);

  const createPayload = async (val: any, method: string) => {
    let payload = {};
    if (["AP", "GP"].includes(method)) {
      const setupIntentId = val?.setupIntent?.id || val?.id || val;
      payload = {
        lead_id: accountLeadData?.lead_id,
        cluster_id: clusterId || userData?.data?.accountData?.clusterid,
        accountid: userData?.data?.accountData?.accountid,
        module: "process_payment",
        x_method: method,
        email: userData?.data?.accountData?.email,
        setupIntent: setupIntentId,
        isEdit: Boolean(isEdit),
        cppid: isEdit ? props.data?.id : 0,
      };
    } else {
      payload = {
        lead_id: accountLeadData?.lead_id,
        cluster_id: userData.data.accountData.clusterid,
        accountid: userData.data.accountData.accountid,
        module: "process_payment",
        setupIntent: val.setupIntent,
        x_method: method,
      };
    }
    return payload;
  };

  const createWalletsProfile = async (val: any, method: string) => {
    setIsBTNLoading(true);
    try {
      let payload = await createPayload(val, method);
      let res: any = await dispatch(walletsProfile(payload));
      if (res.payload.body.success) {
        setSuccessScreen(true);
        const savedWallet = res.payload.body.data.paymentMethod;
        const nextWalletList = isEdit
          ? walletList.map((wallet: any) =>
              wallet.id === savedWallet.id
                ? { ...wallet, ...savedWallet }
                : wallet
            )
          : walletList.some((wallet: any) => wallet.id === savedWallet.id)
            ? walletList
            : [...walletList, savedWallet];
        await dispatch(updateWalletList(nextWalletList));
      } else {
        setFailedScreen(true);
      }
      setIsBTNLoading(false);
    } catch (error) {
      setIsBTNLoading(false);
      console.log("createWalletsProfile error =====", error);
      setFailedScreen(true);
    }
  };

  const handleGooglePay = async (
    success: boolean,
    intent?: unknown
  ): Promise<void> => {
    if (!success || !intent) {
      setFailedScreen(true);
      return;
    }
    await createWalletsProfile({ setupIntent: intent as SetupIntent }, "GP");
  };

  const handleApplePay = async (
    success: boolean,
    intent?: unknown
  ): Promise<void> => {
    const setupIntent = intent as SetupIntent | undefined;
    if (
      !success ||
      !setupIntent?.id?.startsWith("seti_") ||
      setupIntent.status !== "succeeded"
    ) {
      setFailedScreen(true);
      return;
    }
    await createWalletsProfile({ setupIntent }, "AP");
  };

  if (isScreenLoading) {
    return (
      <div
        className="w-full h-full grid place-items-center"
        data-testid={tid("wallet-form-loading")}
        aria-label="Loading wallet form"
      >
        <div className="inline-block h-10 w-10 animate-spin rounded-full border-2 border-solid border-blue border-r-transparent align-[-0.125em] text-primary motion-reduce:animate-[spin_1.5s_linear_infinite]"></div>
      </div>
    );
  }

  if (successScreen) {
    return (
      <div data-testid={tid("wallet-form-success")}>
        <SuccessScreen
          closeLabel="Close wallet confirmation"
          title={isEdit ? "Payment Method Updated" : "Payment Method Added"}
          content={
            isEdit ? (
              <>
                Your payment method details have been successfully updated.{" "}
                <br />
                You’re all set for your next transactions.
              </>
            ) : (
              <>
                Thank you for adding a new payment method to your account. You
                can now use it to make payments.
              </>
            )
          }
          onCloseClick={() => {
            onClose();
            onCloseClick();
          }}
        />
      </div>
    );
  }

  if (failedScreen) {
    return (
      <div data-testid={tid("wallet-form-failed")}>
        <FailedScreen
          title={"Payment failed"}
          content={<>Sorry! Your payment method was declined.</>}
          onCloseClick={() => {
            onClose();
            onCloseClick();
          }}
          clusterId={clusterId}
        />
      </div>
    );
  }
  const handleSetupPaypal = async () => {
    try {
      setIsPayPalLoading(true);
      const payload = {
        is_recurring: "Y",
        phone_number: userData?.data?.accountData?.phone,
        first_name: userData?.data?.accountData?.first_name,
        last_name: userData?.data?.accountData?.last_name,
        accountid: userData?.data?.accountData?.accountid,
        agent: userData?.data?.accountData?.lead_owner,
        lead_owner: userData?.data?.accountData?.lead_owner,
        lead_id: accountLeadData?.lead_id,
        clusterid: clusterId,
        success_url: `${
          window.location.href
        }?method=paypal&status=success&isRecurring=${true}`,
        cancel_url: `${
          window.location.href
        }?method=paypal&status=cancel&isRecurring=${true}`,
        start_date: new Date(),
      };
      let res: any = await dispatch(createPaypalToken(payload));
      if (res?.payload?.body?.success) {
        await dispatch(
          storePayPalOrder({
            url: res?.payload?.body?.result?.url,
            orderId: res?.payload?.body?.result?.invoice_id,
            token: res?.payload?.body?.result?.token,
          })
        );
        // window.location.href = res?.payload?.body?.result?.url;
        setIsPayPalLoading(false);
        window.location.href = res?.payload?.body?.result?.url;
      }
    } catch (error) {
      console.log("createPayPalIntent error =====", error);
    }
  };
  return (
    <div
      className="bg-bgPrimary h-full overflow-y-auto lg:pt-[70px] pt-[56px] select-none"
      data-testid={tid(isEdit ? "edit-wallet-form" : "add-wallet-form")}
      aria-label={isEdit ? "Update wallet form" : "Add wallet form"}
    >
      <div
        className="lg:px-10 px-5 lg:py-5 py-3 bg-bgPrimary flex items-center justify-between fixed top-0 w-full z-10"
        data-testid={tid("wallet-form-header")}
      >
        <div className="flex items-center gap-2">
          {isEdit ? (
            <UserTickIcon aria-hidden="true" />
          ) : (
            <UserAddIcon aria-hidden="true" />
          )}
          <div className="lg:text-[26px] text-[22px] text-txtPrimary font-medium leading-[30px]">
            {isEdit ? "Update wallet" : "Add new digital wallet"}
          </div>
        </div>
        <div className="flex items-center gap-5">
          <div
            role="button"
            tabIndex={0}
            aria-label="Close wallet form"
            data-testid={tid("wallet-form-close-button")}
            onClick={() => onClose()}
            className="cursor-pointer p-2 rounded-full hover:!bg-bgSecondary"
          >
            <CloseIcon aria-hidden="true" />
          </div>
        </div>
      </div>
      <form onSubmit={onSubmit} data-testid={tid("wallet-form-fields")}>
        <div className="mt-5 lg:px-10 px-5 bg-bgPrimary">
          {paymentPermission?.ap_ext_availability === "Y" &&
            stripePromiseApple && (
              <Elements stripe={stripePromiseApple}>
                <ApplePayAvailability
                  show={show}
                  setIsApplePay={setIsApplePay}
                  setIsCheckComplete={setIsApplePayCheckComplete}
                />
              </Elements>
            )}
          <div className="mb-10 flex flex-wrap items-center lg:gap-3 gap-[10px] ">
            {paymentMethodList?.map((val: any) => {
              return (
                <React.Fragment key={val.id || val.name}>
                  {val.display && (
                    <>
                      <div
                        role="button"
                        tabIndex={0}
                        aria-pressed={selectedMethod === val.name}
                        aria-label={`Select ${val.name}`}
                        data-testid={tid(`wallet-method-option-${val.name}`)}
                        className={`text-sm cursor-pointer w-40 border-[1px] px-[15px] py-[11px] rounded-[6px] flex justify-start items-center gap-[6px] ${
                          selectedMethod === val.name
                            ? "border-btnOutlineStroke bg-btnBackGround"
                            : "border-strokePrimary bg-bgPrimary"
                        } hover:!bg-bgSecondary hover:!border-strokeActive`}
                        onClick={() => {
                          setSelectedMethod(val.name);
                          if (val.name === "Google Pay") {
                            setIsGooglePayAvailable(true);
                          }
                          if (
                            (val.name === "Cash App Pay" &&
                              !setupClientSecret) ||
                            (val.name === "Amazon Pay" && !setupAmazonSecret) ||
                            (val.name === "Google Pay" && !setupGooglePaySecret)
                          ) {
                            getSecret(val.name);
                          }
                        }}
                      >
                        {selectedMethod === val.name
                          ? val.activeIcon
                          : val.icon}
                        {val.name}
                      </div>
                    </>
                  )}
                </React.Fragment>
              );
            })}
          </div>
          <div className="mb-10">
            <div className="p-3 w-full flex justify-start gap-2 border-[1px] rounded-[4px] border-strokePrimary ">
              <span className="w-[50px] h-[50px] grid place-items-center">
                <MobileVector />
              </span>
              {selectedMethod === "Cash App Pay" ? (
                <div className="text-sm lg:text-base font-normal leading-[25px] text-txtSecondary pr-3">
                  After submitting your order, scan the QR code using Cash App.
                </div>
              ) : (
                <div className="text-sm lg:text-base font-normal leading-[25px] text-txtSecondary pr-[12px]">
                  Another step will appear to securely submit your payment
                  information.
                </div>
              )}
            </div>
            <div className="mt-4 text-txtParagraph text-sm lg:text-base font-normal">
              {selectedMethod === "Cash App Pay"
                ? `By continuing, you authorize ${companyDetails?.name || companyDetails?.company_name} to debit your Cash App account for this payment and future payments in accordance with ${companyDetails?.name || companyDetails?.company_name}’s terms, until this authorization is revoked. You can change this anytime in your Cash App Settings.`
                : `By continuing, you allow ${companyDetails?.name || companyDetails?.company_name} to charge your ${selectedMethod} for future payments in accordance with their terms.`}
            </div>
          </div>

          {selectedMethod === "Cash App Pay" ? (
            setupClientSecret && stripePromiseCashApp ? (
              <Elements
                stripe={stripePromiseCashApp}
                options={{ clientSecret: setupClientSecret }}
              >
                <CashAppButton isSecretLoading={isSecretLoading} />
              </Elements>
            ) : (
              <Button
                text={"Add"}
                data-testid={tid("wallet-form-add-cash-app-loading-button")}
                aria-label="Add Cash App Pay"
                className="w-full"
                isLoading={isSecretLoading}
              />
            )
          ) : selectedMethod === "Google Pay" && !isApplePay ? (
            isBTnLoading || isSecretLoading ? (
              <div className=" w-full bg-bgHover rounded-[5px] py-[10px] flex justify-center items-center cursor-pointer">
                <div className="inline-block h-5 w-5 animate-spin rounded-full border-2 border-solid border-current border-r-transparent align-[-0.125em] text-primary motion-reduce:animate-[spin_1.5s_linear_infinite]"></div>
              </div>
            ) : !isGooglePayAvailable ? (
              <div className="text-sm text-txtSecondary text-center">
                Google Pay is not available in this browser or wallet.
              </div>
            ) : setupGooglePaySecret && stripePromiseGooglePay ? (
              <Elements
                stripe={stripePromiseGooglePay}
                options={{ clientSecret: setupGooglePaySecret }}
              >
                <StripeGooglePayButton
                  amount={0}
                  allowZeroAmount
                  requestLabel="Save Google Pay"
                  clientSecret={setupGooglePaySecret}
                  disabled={isBTnLoading}
                  handleGooglePay={handleGooglePay}
                  setIsGooglePay={setIsGooglePayAvailable}
                />
              </Elements>
            ) : null
          ) : selectedMethod === "Apple Pay" ? (
            isBTnLoading ? (
              <div className="w-full bg-bgHover rounded-[5px] py-[10px] flex justify-center items-center">
                <div className="inline-block h-5 w-5 animate-spin rounded-full border-2 border-solid border-current border-r-transparent align-[-0.125em] text-primary motion-reduce:animate-[spin_1.5s_linear_infinite]"></div>
              </div>
            ) : !isApplePay ? (
              <div className="text-sm text-txtSecondary text-center">
                Apple Pay is not available in this browser or wallet.
              </div>
            ) : (
              <StripeApplePayButton
                amount={0}
                allowZeroAmount
                requestLabel="Save Apple Pay"
                isRecurring
                isWalletProfile
                useStripeApplePay
                isApplePay={isApplePay}
                setIsApplePay={setIsApplePay}
                clusterIdOverride={
                  clusterId || userData?.data?.accountData?.clusterid
                }
                leadId={accountLeadData?.lead_id}
                disabled={isBTnLoading}
                handleApplePay={handleApplePay}
              />
            )
          ) : selectedMethod === "Amazon Pay" ? (
            setupAmazonSecret && stripePromiseAmazon ? (
              <Elements
                stripe={stripePromiseAmazon}
                options={{ clientSecret: setupAmazonSecret }}
              >
                <AmazonPayButton isSecretLoading={isSecretLoading} />
              </Elements>
            ) : (
              <Button
                text={"Add"}
                data-testid={tid("wallet-form-add-amazon-pay-loading-button")}
                aria-label="Add Amazon Pay"
                className="w-full"
                isLoading={isSecretLoading}
              />
            )
          ) : (
            <Button
              text={"Add"}
              data-testid={tid("wallet-form-add-paypal-button")}
              aria-label="Add PayPal"
              className="w-full"
              isLoading={isPayPalLoding}
              onClick={() => handleSetupPaypal()}
            />
          )}
        </div>
      </form>
    </div>
  );
};

const CashAppButton = ({ isSecretLoading }: { isSecretLoading?: boolean }) => {
  const tid = useTid();
  const stripe = useStripe();
  const elements = useElements();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const userData = useUserData();
  const handleSetup = async () => {
    setIsLoading(true);
    if (!stripe || !elements) {
      setIsLoading(false);
      return;
    }

    let payload = {};
    if (userData?.data?.accountData?.country) {
      payload = {
        ...payload,
        country: userData?.data?.accountData?.country ?? "US",
      };
    }
    if (userData?.data?.accountData?.city) {
      payload = {
        ...payload,
        city: userData?.data?.accountData?.city ?? "",
      };
    }
    if (userData?.data?.accountData?.postal_code) {
      payload = {
        ...payload,
        postal_code: userData?.data?.accountData?.postal_code ?? "",
      };
    }
    if (userData?.data?.accountData?.state) {
      payload = {
        ...payload,
        state: userData?.data?.accountData?.state ?? "",
      };
    }
    if (userData?.data?.accountData?.address1) {
      payload = {
        ...payload,
        line1: userData?.data?.accountData?.address1 ?? "",
      };
    }
    if (userData?.data?.accountData?.address2) {
      payload = {
        ...payload,
        line2: userData?.data?.accountData?.address2 ?? "",
      };
    }

    const { error } = await stripe.confirmSetup({
      elements,
      confirmParams: {
        return_url: `${window.location.href}?payment_method=CAPP&checked=false`,
        payment_method_data: {
          billing_details: {
            name: userData?.data?.accountData?.first_name,
            email: userData?.data?.accountData?.email,
            address: {
              ...payload,
            },
          },
        },
      },
    });
    setIsLoading(false);

    if (error) {
      console.log(error.message);
    }
  };
  return (
    <>
      <div className="none hidden">
        <PaymentElement />
      </div>
      <Button
        text={"Add"}
        data-testid={tid("wallet-form-add-cash-app-button")}
        aria-label="Add Cash App Pay"
        className="w-full"
        onClick={() => handleSetup()}
        isLoading={isSecretLoading || isLoading}
      />
    </>
  );
};

const ApplePayAvailability = ({
  show,
  setIsApplePay,
  setIsCheckComplete,
}: {
  show: boolean;
  setIsApplePay: (available: boolean) => void;
  setIsCheckComplete: (complete: boolean) => void;
}) => {
  const stripe = useStripe();

  useEffect(() => {
    if (!stripe || !show) {
      return;
    }
    let isActive = true;
    setIsCheckComplete(false);
    const pr = stripe.paymentRequest({
      country: "US",
      currency: "usd",
      total: {
        label: "Save Apple Pay",
        amount: 0,
      },
      requestPayerName: true,
      requestPayerEmail: true,
    });

    pr.canMakePayment()
      .then((result) => {
        if (isActive) setIsApplePay(Boolean(result?.applePay));
      })
      .catch(() => {
        if (isActive) setIsApplePay(false);
      })
      .finally(() => {
        if (isActive) setIsCheckComplete(true);
      });

    return () => {
      isActive = false;
    };
  }, [setIsApplePay, setIsCheckComplete, show, stripe]);

  return null;
};

const AmazonPayButton = ({
  isSecretLoading,
}: {
  isSecretLoading?: boolean;
}) => {
  const tid = useTid();
  const stripe = useStripe();
  const elements = useElements();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const userData = useUserData();
  const handleSetup = async () => {
    setIsLoading(true);
    if (!stripe || !elements) {
      setIsLoading(false);
      return;
    }

    let payload = {};
    if (userData?.data?.accountData?.country) {
      payload = {
        ...payload,
        country: userData?.data?.accountData?.country ?? "US",
      };
    }
    if (userData?.data?.accountData?.city) {
      payload = {
        ...payload,
        city: userData?.data?.accountData?.city ?? "",
      };
    }
    if (userData?.data?.accountData?.postal_code) {
      payload = {
        ...payload,
        postal_code: userData?.data?.accountData?.postal_code ?? "",
      };
    }
    if (userData?.data?.accountData?.state) {
      payload = {
        ...payload,
        state: userData?.data?.accountData?.state ?? "",
      };
    }
    if (userData?.data?.accountData?.address1) {
      payload = {
        ...payload,
        line1: userData?.data?.accountData?.address1 ?? "",
      };
    }
    if (userData?.data?.accountData?.address2) {
      payload = {
        ...payload,
        line2: userData?.data?.accountData?.address2 ?? "",
      };
    }

    const { error } = await stripe.confirmSetup({
      elements,
      confirmParams: {
        return_url: `${window.location.href}?payment_method=AMZP&checked=false`,
        payment_method_data: {
          billing_details: {
            name: userData?.data?.accountData?.first_name,
            email: userData?.data?.accountData?.email,
            address: {
              ...payload,
            },
          },
        },
      },
    });
    setIsLoading(false);

    if (error) {
      console.log(error.message);
    }
  };
  return (
    <>
      <div className="none hidden">
        <PaymentElement />
      </div>
      <Button
        text={"Add"}
        data-testid={tid("wallet-form-add-amazon-pay-button")}
        aria-label="Add Amazon Pay"
        className="w-full"
        onClick={() => handleSetup()}
        isLoading={isSecretLoading || isLoading}
      />
    </>
  );
};

export default AddEditWalletForm;
